// OnboardingContext.tsx
import CookingHabitsStep from "@/components/Auth/Onboarding/CookingHabits/CookingHabits";
import RecipeSelectionStep from "@/components/Auth/Onboarding/RecipeSelection/RecipeSelection";
import WelcomeStep from "@/components/Auth/Onboarding/Welcome/Welcome";
import { createContext, ReactNode, useContext, useState } from "react";

export type OnboardingStepType = {
  key: string;
  component: React.ComponentType<Record<string, unknown>>;
  isSkippable: boolean;
  hidden?: boolean;
  skip?: boolean;
  image?: {
    url: string;
    title?: string;
  };
  isFollowUpStep?: boolean;
};

export type OnboardingProgressType = {
  key: string;
  viewed: boolean;
  completed: boolean;
};

type UserAnswers = Record<string, unknown>;

type OnboardingContextType = {
  currentStepIndex: number;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  onboardingSteps: OnboardingStepType[];
  setOnboardingSteps: React.Dispatch<
    React.SetStateAction<OnboardingStepType[]>
  >;
  onboardingProgress: OnboardingProgressType[];
  updateProgress: (key: string, viewed: boolean, completed: boolean) => void;
  userAnswers: UserAnswers;
  setUserAnswer: (stepKey: string, answer: unknown) => void;
  addDynamicStep: (step: OnboardingStepType) => void;
  removeDynamicStep: (stepKey: string) => void;
  isOptionSelected: boolean;
  setIsOptionSelected: (val: boolean) => void;
};

const initialContextValue: OnboardingContextType = {
  currentStepIndex: 0,
  setCurrentStepIndex: () => {
    // No-op placeholder
  },
  onboardingSteps: [],
  setOnboardingSteps: () => {
    // No-op placeholder
  },
  onboardingProgress: [],
  updateProgress: () => {
    // No-op placeholder
  },
  userAnswers: {},
  setUserAnswer: () => {
    // No-op placeholder
  },
  addDynamicStep: () => {
    // No-op placeholder
  },
  removeDynamicStep: () => {
    // No-op placeholder
  },
  isOptionSelected: false,
  setIsOptionSelected: () => {
    // No-op placeholder
  },
};

const OnboardingContext =
  createContext<OnboardingContextType>(initialContextValue);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [onboardingProgress, setOnboardingProgress] = useState<
    OnboardingProgressType[]
  >([
    { key: "welcome", viewed: true, completed: false },
    { key: "recipeSelection", viewed: true, completed: false },
    { key: "cookingHabits", viewed: true, completed: false },
  ]);

  const [isOptionSelected, setIsOptionSelected] = useState(false);

  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStepType[]>([
    { key: "welcome", component: WelcomeStep, isSkippable: false },
    {
      key: "recipeSelection",
      component: RecipeSelectionStep,
      isSkippable: true,
    },
    { key: "cookingHabits", component: CookingHabitsStep, isSkippable: true },
  ]);

  const [onboardingAnswers, setOnboardingAnswers] = useState({});

  const setUserAnswer = (key: string, value: unknown) => {
    setOnboardingAnswers((prev) => {
      const updatedAnswer = { ...prev, [key]: value };

      // Store in sessionStorage on each update
      sessionStorage.setItem(
        "onboardingAnswers",
        JSON.stringify(updatedAnswer)
      );

      return updatedAnswer;
    });
  };

  const updateProgress = (key: string, viewed: boolean, completed: boolean) => {
    setOnboardingProgress((prev) =>
      prev.map((step) =>
        step.key === key ? { ...step, viewed, completed } : step
      )
    );
  };

  const addDynamicStep = (step: OnboardingStepType) => {
    setOnboardingSteps((prev) => {
      const exists = prev.find((s) => s.key === step.key);
      if (exists) return prev;
      const newSteps = [...prev];
      newSteps.splice(currentStepIndex + 1, 0, step);
      return newSteps;
    });

    setOnboardingProgress((prev) => {
      const exists = prev.find((p) => p.key === step.key);
      if (exists) return prev;
      const newProgress = [...prev];
      newProgress.splice(currentStepIndex + 1, 0, {
        key: step.key,
        viewed: false,
        completed: false,
      });
      return newProgress;
    });
  };

  const removeDynamicStep = (stepKey) => {
    const newSteps = onboardingSteps.filter((step) => step.key !== stepKey);
    const newProgress = onboardingProgress.filter(
      (step) => step.key !== stepKey
    );
    setOnboardingSteps(newSteps);
    setOnboardingProgress(newProgress);
  };

  return (
    <OnboardingContext.Provider
      value={{
        currentStepIndex,
        setCurrentStepIndex,
        onboardingSteps,
        setOnboardingSteps,
        onboardingProgress,
        updateProgress,
        userAnswers: onboardingAnswers,
        setUserAnswer,
        addDynamicStep,
        removeDynamicStep,
        isOptionSelected,
        setIsOptionSelected,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboardingContext must be used within OnboardingProvider"
    );
  }
  return context;
};
